const ua = navigator.userAgent || "";
export const isIOS =
  /iPad|iPhone|iPod/.test(ua) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isAndroid = /Android/.test(ua);
export const isAndroidWebView = isAndroid && /; ?wv\)/.test(ua);
export const isChromeLike =
  /Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua) && !isIOS;
// iOS: always show the escape button — WKWebView's UA is often indistinguishable
// from real mobile Safari, so we bias safe rather than risk hiding it in the app.
// Android: only when the webview token is present; real Chrome gets native
// Cast/AirPlay controls in the player bar instead.
export const showEscape = isIOS || isAndroidWebView;

// Proven mechanism (confirmed opening a real Chrome Custom Tab): a plain
// <a href> with no click-time JS, pointed at a genuinely foreign domain. The
// app hands off navigation to a different domain to Chrome, but stays in-app
// for navigation to Kajabi's own domain — so any fallback used before a real
// handoff link has been minted must point here, never at a Kajabi URL, or a
// tap during that window silently reopens the same in-app screen.
export const STANDALONE_PLAYER_URL = "https://cika-player.pages.dev/";
// Same standalone site, but tells main.tsx to render the recordings
// playlist instead of the live view when there's no token yet to infer
// that from (e.g. a fallback link used before /mint has resolved). Without
// the query param, a bare STANDALONE_PLAYER_URL always defaults to live.
export const STANDALONE_RECORDINGS_URL = "https://cika-player.pages.dev/?mode=recordings";

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

// Whether we're running inside the branded app's WebView. The UA "; wv)"
// marker is the usual tell, but some WebView configurations omit it — so
// the kc-bma cookie (set by the app shell itself, confirmed present on
// requests from inside it) is accepted as an equally valid signal. Used to
// decide layout, not access: getting this wrong must never grant anything.
export function isInAppShell(): boolean {
  return isAndroidWebView || isIOS || getCookie("kc-bma") === "true";
}

export type Identity = {
  contactId?: string;
  validationToken?: string;
  communityToken?: string;
  refreshToken?: string;
  kcBma?: string;
  source: string;
};

// Sends raw tokens, not client-decoded claims — the worker independently
// verifies identity (by calling Kajabi's own API with the token), rather
// than trusting whatever this page's own JS says its decoded payload
// contains. In a normal browser tab, Kajabi exposes window.Kajabi.
// currentSiteUser plus a signed window.validationToken. Inside the app
// webview neither of those exist; the closest equivalent there is the
// community_id_token/community_refresh_token JWT cookie pair.
export function getIdentityForHandoff(): Identity {
  const kajabiUser = (window as unknown as { Kajabi?: { currentSiteUser?: { contactId?: string; id?: string } } }).Kajabi?.currentSiteUser;
  const validationToken = (window as unknown as { validationToken?: string }).validationToken;

  if (kajabiUser || validationToken) {
    return {
      contactId: kajabiUser?.contactId ?? kajabiUser?.id,
      validationToken,
      source: "browser",
    };
  }

  const communityToken = getCookie("community_id_token");
  if (communityToken) {
    return {
      communityToken,
      refreshToken: getCookie("community_refresh_token") ?? undefined,
      source: isAndroidWebView ? "android-webview" : "ios-webview",
    };
  }

  // No community access at all (e.g. a course-only buyer) — the worker
  // falls back to checking the request's own X-Requested-With header, but
  // that alone is thin on its own, so this cookie (set by the branded app
  // shell itself, confirmed present on requests inside it) is sent too as
  // a second, independent signal the worker cross-checks against the header.
  return {
    kcBma: getCookie("kc-bma") ?? undefined,
    source: isAndroidWebView ? "android-webview" : isIOS ? "ios-webview" : "unknown",
  };
}
